import CreatableSelect from "react-select/creatable";
import { useContext, useEffect } from "react";
import { isEmpty } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal, ModalBody, ModalHeader } from "react-bootstrap";

import Icon from "../../../components/Icon";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import TableFilter from "../../../components/TableFilter";
import SearchTable from "../../../components/SearchTable";
import EmptyData from "../../../components/EmptyData";
import PaginationComponent from "../../../components/Pagination";
import ErrorMessage from "../../../components/ErrorMessage";
import BooleanIcon from "../../../components/BooleanIcon";
import { fieldAdminContext } from "../../../context/AdminContext/FieldAdminContext/FieldAdminContext";
import { createFieldValidation } from "../../../utils/validation/validation";
import { convertDate } from "../../../utils";
import {
  convertFieldTypeToText,
  exporterData,
  fieldTypeItems,
} from "../../../utils/enum";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";
import DataExporter from "../../../components/DataExporter";

const FieldsAdmin = () => {
  const {
    fields,
    field,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    type,
    setType,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    isShowModal,
    handleShowModal,
    handleHideModal,
    handleGetFields,
    handleCreateField,
    handleEditField,
    handleDeleteField,
  } = useContext(fieldAdminContext);

  useEffect(() => {
    handleGetFields();
  }, [start, end, pageNumber, type, itemsCount, sort]);

  // Validation
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { isRequired: false },
    resolver: yupResolver(createFieldValidation),
  });

  return (
    <>
      <SiteTitle title="فیلدها" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
        fieldType={type}
        setFieldType={setType}
      />

      <div className="card h-100 p-0 radius-12">
        <SearchTable
          endItem={
            <div className="d-flex justify-content-between align-items-center gap-2">
              {!isEmpty(fields) && (
                <DataExporter
                  headers={exporterData.fields.headers}
                  keys={exporterData.fields.keys}
                  data={fields}
                  fileName={exporterData.fields.fileName}
                />
              )}
              <button
                className="btn btn-primary text-sm btn-sm radius-8 d-flex align-items-center gap-2"
                onClick={() => {
                  handleShowModal();
                  reset();
                }}
              >
                <Icon icon="ic:baseline-plus" className="text-xl" />
                افزودن فیلد
              </button>
            </div>
          }
          filter={filter}
          setFilter={setFilter}
          onSearch={handleGetFields}
        />
        <div className="card-body p-24">
          {isEmpty(fields) ? (
            <EmptyData />
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">لیبل</th>
                      <th scope="col">نام</th>
                      <th scope="col">نوع</th>
                      <th scope="col">اجباری</th>
                      <th scope="col">تاریخ ایجاد</th>
                      <th scope="col">تاریخ آخرین ویرایش </th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((item, index) => (
                      <tr>
                        <td>{index + 1}</td>
                        <td>{item.label}</td>
                        <td>{item.name}</td>
                        <td>{convertFieldTypeToText(item.type)}</td>
                        <td>
                          <BooleanIcon value={item.isRequired} />
                        </td>
                        <td>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                        </td>
                        <td>
                          {convertDate(item.updatedAt, "YYYY/MM/DD - HH:mm")}
                        </td>
                        <td>
                          <div className="dropdown">
                            <button
                              className="hover-bg-transparent"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <Icon
                                icon="ph:dots-three-outline-fill"
                                className="menu-icon"
                              />
                            </button>
                            <ul className="dropdown-menu list">
                              <li
                                onClick={() => {
                                  reset();
                                  handleShowModal(item, setValue);
                                }}
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                              >
                                ویرایش
                              </li>

                              <li className="dropdown-divider" />

                              <li
                                onClick={() =>
                                  areYouSureMessage(
                                    () => handleDeleteField(item._id),
                                    `آیا از حذف فیلد ${item.firstName} ${item.lastName} مطمئنید؟`
                                  )
                                }
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-danger"
                              >
                                حذف
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            </>
          )}
        </div>
      </div>

      <Modal
        show={isShowModal}
        className="modal-dialog-centered"
        size="lg"
        onHide={handleHideModal}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">
            {isEmpty(field) ? "افزودن فیلد جدید" : "ویرایش فیلد"}
          </span>
        </ModalHeader>
        <ModalBody>
          <form
            autoComplete="off"
            onSubmit={handleSubmit(
              isEmpty(field) ? handleCreateField : handleEditField
            )}
          >
            <div className="row">
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="label"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      لیبل
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="label"
                      placeholder="لیبل را وارد نمایید"
                      {...register("label")}
                    />
                  </div>

                  <ErrorMessage message={errors.label?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="name"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      نام
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="name"
                      placeholder="نام را وارد نمایید"
                      {...register("name")}
                    />
                  </div>

                  <ErrorMessage message={errors.name?.message} />
                </div>
              </div>

              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="type"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      نوع
                    </label>
                    <select
                      className="form-control radius-8"
                      {...register("type")}
                    >
                      <option value="" selected hidden>
                        نوع را انتخاب نمایید
                      </option>
                      {fieldTypeItems.map((item) => (
                        <option
                          key={item}
                          value={item}
                          selected={watch("type") === item}
                        >
                          {convertFieldTypeToText(item)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <ErrorMessage message={errors.type?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="isRequired"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      وضعیت اجباری بودن
                    </label>
                    <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative">
                      <label
                        htmlFor="isRequired"
                        className="position-absolute w-100 h-100 start-0 top-0"
                      />
                      <div className="d-flex align-items-center gap-3 justify-content-between">
                        <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                          اجباری
                        </span>
                        <Controller
                          control={control}
                          render={({ field: { value, onChange, onBlur } }) => (
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="isRequired"
                              onChange={(e) => {
                                const value = e.target.checked;
                                onChange(value);
                              }}
                              onBlur={onBlur}
                              checked={value}
                            />
                          )}
                          name="isRequired"
                        />
                      </div>
                    </div>
                  </div>

                  <ErrorMessage message={errors.isRequired?.message} />
                </div>
              </div>

              {[fieldTypeItems[6], fieldTypeItems[7]].includes(
                watch("type")
              ) && (
                <div className="col-12">
                  <div className="mb-20">
                    <div>
                      <label
                        htmlFor="name"
                        className="form-label fw-semibold text-primary-light text-sm mb-8"
                      >
                        گزینه ها
                      </label>
                      <Controller
                        control={control}
                        render={({ field: { value, onChange, onBlur } }) => (
                          <CreatableSelect
                            isMulti
                            isClearable
                            noOptionsMessage={() =>
                              "گزینه ای برای نمایش یافت نشد"
                            }
                            formatCreateLabel={(val) => `افزودن '${val}'`}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            onChange={(e) => onChange(e)}
                            value={value}
                            options={value}
                            onBlur={onBlur}
                            placeholder="گزینه ها را وارد کنید"
                          />
                        )}
                        name="options"
                      />
                    </div>

                    <ErrorMessage message={errors.options?.message} />
                  </div>
                </div>
              )}
            </div>
            <div className="text-end mt-3">
              <button
                type="submit"
                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
              >
                {isEmpty(field) ? "افزودن" : "ویرایش"} فیلد
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>
    </>
  );
};

export default FieldsAdmin;
